/**
 * @fileoverview Color-variant helpers for cart line items.
 *
 * Designed for footwear colorways today, but product-agnostic so apparel,
 * accessories, etc. can reuse the same cart identity rules later.
 *
 * Cart identity = `item_id` + optional `color_id`.
 * - Catalog / wishlist “Add to cart” → thumbnail mode (no color_id).
 * - Product detail “Add to cart” → selected color (color_id set).
 */

/** Footwear views, in display order. ``main`` is the hero image. */
export const FOOTWEAR_VARIANT_VIEWS = ["main", "top", "bottom", "pair"];

/**
 * Apparel views, in display order. The first key is the hero image.
 * Add a view here and on the server ``APPAREL_VIEW_KEYS`` together.
 * Views the API sends that are not in this list still appear after these.
 */
export const APPAREL_VARIANT_VIEWS = ["undressed", "dressed"];

const KNOWN_VARIANT_VIEWS = [...FOOTWEAR_VARIANT_VIEWS, ...APPAREL_VARIANT_VIEWS];

/**
 * Ordered view entries for a colorway. Known views first, then any future keys.
 * @param {object|null|undefined} views
 * @returns {{ key: string, url: string }[]}
 */
export function orderedVariantViews(views) {
  if (!views || typeof views !== "object") return [];
  const used = new Set();
  const items = [];
  for (const key of KNOWN_VARIANT_VIEWS) {
    if (views[key]) {
      items.push({ key, url: views[key] });
      used.add(key);
    }
  }
  for (const key of Object.keys(views)) {
    if (!used.has(key) && views[key]) {
      items.push({ key, url: views[key] });
    }
  }
  return items;
}

/**
 * Hero image for a colorway: footwear ``main``, otherwise the first stored view.
 * @param {{ views?: object }|null|undefined} variant
 * @returns {string}
 */
export function primaryVariantImage(variant) {
  const views = variant?.views || {};
  if (views.main) return views.main;
  return orderedVariantViews(views)[0]?.url || "";
}

/** How the cart line’s color was chosen. */
export const COLOR_VARIANT_SOURCE = Object.freeze({
  /** Product card / wishlist: use listing thumbnail, no committed color. */
  THUMBNAIL: "thumbnail",
  /** Product detail: user committed a colorway. */
  SELECTED: "selected",
});

/**
 * Normalize a color id for stable line matching.
 * @param {unknown} colorId
 * @returns {string|null}
 */
export function normalizeColorId(colorId) {
  if (colorId == null || colorId === "") return null;
  const s = String(colorId).trim().toLowerCase();
  return s || null;
}

/**
 * Strip leading `#` from a hex string; returns null if empty.
 * @param {unknown} hex
 * @returns {string|null}
 */
export function stripColorHash(hex) {
  if (hex == null || hex === "") return null;
  const s = String(hex).trim().replace(/^#/, "").toLowerCase();
  return s || null;
}

/**
 * Stable cart line key: `itemId` or `itemId::colorId`.
 * @param {unknown} itemId
 * @param {unknown} [colorId]
 * @returns {string}
 */
export function buildCartLineKey(itemId, colorId = null) {
  const id = String(itemId ?? "");
  const color = normalizeColorId(colorId);
  return color ? `${id}::${color}` : id;
}

/**
 * @param {{ item_id?: unknown, color_id?: unknown }|null|undefined} line
 * @returns {string}
 */
export function cartLineKeyFromItem(line) {
  if (!line) return "";
  return buildCartLineKey(line.item_id, line.color_id);
}

/**
 * Whether two cart-shaped objects refer to the same line.
 * @param {{ item_id?: unknown, color_id?: unknown }|null|undefined} a
 * @param {{ item_id?: unknown, color_id?: unknown }|null|undefined} b
 * @returns {boolean}
 */
export function cartLinesMatch(a, b) {
  if (!a || !b) return false;
  return cartLineKeyFromItem(a) === cartLineKeyFromItem(b);
}

/**
 * Find a cart line by product id + optional color id.
 * @param {Array<{ item_id?: unknown, color_id?: unknown }>} items
 * @param {{ item_id: unknown, color_id?: unknown }} ref
 * @returns {object|undefined}
 */
export function findCartLine(items, ref) {
  if (!Array.isArray(items) || !ref) return undefined;
  const key = buildCartLineKey(ref.item_id, ref.color_id);
  return items.find((i) => buildCartLineKey(i.item_id, i.color_id) === key);
}

/**
 * Pick the best thumbnail image for a product card / wishlist line.
 * Prefers explicit product.thumbnail, then first gallery image.
 * @param {object|null|undefined} product
 * @returns {string|null}
 */
export function resolveThumbnailImage(product) {
  if (!product) return null;
  if (product.thumbnail) return product.thumbnail;
  if (product.image_url) return product.image_url;
  if (Array.isArray(product.images) && product.images.length > 0) {
    return product.images[0];
  }
  return null;
}

/**
 * Resolve color + thumbnail fields to merge into a cart payload.
 *
 * @param {object} product
 * @param {object} [options]
 * @param {object|null} [options.selectedVariant] - `{ id, primary, secondary, label, views }`
 * @param {'thumbnail'|'selected'} [options.mode]
 * @returns {{
 *   color_id: string|null,
 *   color_label: string|null,
 *   color_primary: string|null,
 *   color_secondary: string|null,
 *   color_source: string,
 *   thumbnail: string|null,
 * }}
 */
export function resolveColorVariantForCart(
  product,
  { selectedVariant = null, mode = COLOR_VARIANT_SOURCE.THUMBNAIL } = {}
) {
  const useSelected =
    mode === COLOR_VARIANT_SOURCE.SELECTED &&
    selectedVariant &&
    (selectedVariant.id != null || selectedVariant.color_id != null);

  if (useSelected) {
    const colorId = normalizeColorId(
      selectedVariant.id ?? selectedVariant.color_id
    );
    const primary = stripColorHash(selectedVariant.primary);
    const secondary = stripColorHash(selectedVariant.secondary);
    let label = (selectedVariant.label || "").trim() || null;
    if (!label && primary && secondary) {
      label = `#${primary} / #${secondary}`;
    } else if (!label && primary) {
      label = `#${primary}`;
    }

    return {
      color_id: colorId,
      color_label: label,
      color_primary: primary,
      color_secondary: secondary,
      color_source: COLOR_VARIANT_SOURCE.SELECTED,
      thumbnail:
        primaryVariantImage(selectedVariant) ||
        resolveThumbnailImage(product),
    };
  }

  return {
    color_id: null,
    color_label: null,
    color_primary: null,
    color_secondary: null,
    color_source: COLOR_VARIANT_SOURCE.THUMBNAIL,
    thumbnail: resolveThumbnailImage(product),
  };
}

/**
 * Merge product fields with resolved color variant into a cart-ready object.
 * Callers may still override quantity, pricing, etc.
 *
 * @param {object} product
 * @param {Parameters<typeof resolveColorVariantForCart>[1]} [options]
 * @returns {object}
 */
export function buildCartProductPayload(product, options) {
  const color = resolveColorVariantForCart(product, options);
  return {
    ...product,
    ...color,
  };
}
